var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { BlockTypeSelect, BoldItalicUnderlineToggles, CodeToggle, CreateLink, DialogButton, headingsPlugin, imagePlugin, insertImage$, InsertTable, InsertThematicBreak, linkDialogPlugin, linkPlugin, listsPlugin, ListsToggle, MDXEditor, quotePlugin, Separator, tablePlugin, thematicBreakPlugin, toolbarPlugin, UndoRedo } from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import Box from "@mui/material/Box";
import { usePublisher } from "@mdxeditor/gurx";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import { isSupportedMarkdownImageUrl } from "../utils/markdownImage.util";
export { isSupportedMarkdownImageUrl };
var InsertImageUrl = function () {
    var t = useTranslation().t;
    var insertImage = usePublisher(insertImage$);
    return (_jsx(DialogButton, { tooltipTitle: t("Insert image"), dialogInputPlaceholder: t("Paste PNG or JPG image URL"), submitButtonTitle: t("Insert image"), buttonContent: _jsx(AddPhotoAlternateIcon, { fontSize: "small" }), onSubmit: function (value) {
            var src = value.trim();
            if (!isSupportedMarkdownImageUrl(src)) {
                window.alert(t("Use a PNG or JPG image URL."));
                return;
            }
            insertImage({ src: src, altText: "", title: "" });
        } }));
};
var getPlugins = function (enableImages) { return __spreadArray(__spreadArray([
    toolbarPlugin({
        toolbarClassName: 'my-classname',
        toolbarContents: function () { return (_jsxs(_Fragment, { children: [_jsx(UndoRedo, {}), _jsx(Separator, {}), _jsx(BlockTypeSelect, {}), _jsx(Separator, {}), _jsx(BoldItalicUnderlineToggles, {}), _jsx(Separator, {}), _jsx(ListsToggle, {}), _jsx(Separator, {}), _jsx(InsertTable, {}), _jsx(CreateLink, {}), enableImages && _jsx(InsertImageUrl, {}), _jsx(InsertThematicBreak, {}), _jsx(Separator, {}), _jsx(CodeToggle, {})] })); }
    }),
    listsPlugin(),
    quotePlugin(),
    headingsPlugin({ allowedHeadingLevels: [1, 2, 3, 4] }),
    linkPlugin(),
    linkDialogPlugin()
], (enableImages ? [imagePlugin({
        disableImageResize: true,
        disableImageSettingsButton: true,
    })] : []), true), [
    tablePlugin(),
    thematicBreakPlugin(),
], false); };
var mdEditorSx = {
    "& .mdxeditor-root-contenteditable blockquote, & [contenteditable='true'] blockquote": {
        borderLeft: "4px solid",
        borderLeftColor: "divider",
        color: "text.secondary",
        marginLeft: 0,
        marginTop: 1,
        marginBottom: 1,
        paddingLeft: 2,
    },
    "& .mdxeditor-root-contenteditable blockquote > *:first-of-type, & [contenteditable='true'] blockquote > *:first-of-type": {
        marginTop: 0,
    },
    "& .mdxeditor-root-contenteditable blockquote > *:last-child, & [contenteditable='true'] blockquote > *:last-child": {
        marginBottom: 0,
    },
};
var MdEditor = forwardRef(function (_a, ref) {
    var initialValue = _a.value, onChangeCallback = _a.onChange, _b = _a.placeholder, placeholder = _b === void 0 ? "Type text" : _b, _c = _a.debounceDelay, debounceDelay = _c === void 0 ? 1000 : _c, style = _a.style, autoFocus = _a.autoFocus, _d = _a.readOnly, readOnly = _d === void 0 ? false : _d, className = _a.className, _e = _a.enableImages, enableImages = _e === void 0 ? false : _e;
    var t = useTranslation().t;
    var mdEditorRef = useRef(null);
    var _f = useState(initialValue || ''), internalValue = _f[0], setInternalValue = _f[1];
    var isInternalChangeRef = useRef(false);
    var plugins = useMemo(function () { return getPlugins(enableImages); }, [enableImages]);
    useEffect(function () {
        var _a;
        if (initialValue !== internalValue && !isInternalChangeRef.current) {
            setInternalValue(initialValue || '');
            (_a = mdEditorRef.current) === null || _a === void 0 ? void 0 : _a.setMarkdown(initialValue || '');
        }
        isInternalChangeRef.current = false;
    }, [initialValue, internalValue]);
    var handleChange = function (md) {
        isInternalChangeRef.current = true;
        setInternalValue(md);
        onChangeCallback === null || onChangeCallback === void 0 ? void 0 : onChangeCallback(md);
    };
    useImperativeHandle(ref, function () { return ({
        setContent: function (content) {
            var _a;
            setInternalValue(content);
            (_a = mdEditorRef.current) === null || _a === void 0 ? void 0 : _a.setMarkdown(content);
        },
        insertImageUrl: function (src) {
            var _a;
            (_a = mdEditorRef.current) === null || _a === void 0 ? void 0 : _a.focus(function () {
                var _a;
                (_a = mdEditorRef.current) === null || _a === void 0 ? void 0 : _a.insertMarkdown("![](".concat(src, ")"));
            });
        }
    }); });
    return (_jsx(Box, __assign({ className: className, style: __assign({ width: '100%', position: 'relative' }, style), sx: mdEditorSx }, { children: _jsx(MDXEditor, { ref: mdEditorRef, markdown: internalValue, onChange: readOnly ? undefined : handleChange, placeholder: t(placeholder, { defaultValue: placeholder }), translation: function (key, defaultValue, interpolations) { return t(key, __assign({ defaultValue: defaultValue }, interpolations)); }, plugins: plugins, autoFocus: autoFocus, readOnly: readOnly }) })));
});
export default MdEditor;
